import { NavLink } from "react-router-dom";

import { DataTableCard } from "../components/DataTableCard";
import { GroundedVisualStrip } from "../components/GroundedVisualStrip";
import { KpiRail } from "../components/KpiRail";
import { NumberedListCard } from "../components/NumberedListCard";
import { PremiumHero } from "../components/PremiumHero";
import { SignalPill } from "../components/SignalPill";
import { StatusChip } from "../components/StatusChip";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";

type SiteZoningPageData = Awaited<ReturnType<typeof loadSiteZoningPage>>;

async function loadSiteZoningPage() {
  const [site, parcel, zoning] = await Promise.all([api.getSite(), api.getParcel(), api.getZoning()]);
  return { site, parcel, zoning };
}

function formatCurrency(value: number) {
  return `$${value.toLocaleString()}`;
}

function formatBoolean(value: boolean, positive = "Yes", negative = "No") {
  return value ? positive : negative;
}

function SiteZoningContent({ data }: { data: SiteZoningPageData }) {
  const { site, parcel, zoning } = data;
  const { keyFacts, siteSummary } = site;
  const combinedGateCount = siteSummary.openItems.length + parcel.openItems.length + zoning.openItems.length;

  const siteFactsRows = [
    ["Property", parcel.keyFacts.propertyAddress],
    ["Parcel ID", parcel.keyFacts.parcelId],
    ["Zoning district", `${zoning.keyFacts.zoning} | ${zoning.keyFacts.zoningDescription}`],
    [
      "Lot area",
      `${parcel.keyFacts.parcelArea.squareFeet.toLocaleString()} sf | ${parcel.keyFacts.parcelArea.acres} acres`
    ],
    ["Lot geometry", `${parcel.keyFacts.frontageFeet} ft frontage | ${parcel.keyFacts.depthFeet} ft depth`],
    ["Property class", parcel.keyFacts.propertyClass],
    ["Assessed value", formatCurrency(parcel.keyFacts.taxAssessedValue)],
    ["County / coordinates", `${parcel.keyFacts.county} | ${parcel.keyFacts.coordinates}`]
  ];

  const physicalPostureRows = [
    [
      "Flood posture",
      `${siteSummary.floodStatus.label} | ${siteSummary.floodStatus.confidence}`,
      `${siteSummary.floodStatus.summary} Survey certified: ${formatBoolean(siteSummary.floodStatus.surveyCertified)}`
    ],
    [
      "Slope posture",
      `${siteSummary.slopeStatus.label} | ${siteSummary.slopeStatus.confidence}`,
      siteSummary.slopeStatus.summary
    ],
    ["GIS flags", siteSummary.gisFlagStatus.label, siteSummary.gisFlagStatus.summary],
    ["Lead concept fit", keyFacts.leadConcept, siteSummary.leadConceptFit]
  ];

  const utilityRows = [
    ["Utility posture", siteSummary.utilityStatus.label, siteSummary.utilityStatus.summary],
    [
      "City service",
      `${formatBoolean(keyFacts.cityWaterAvailable)} water | ${formatBoolean(keyFacts.citySewerAvailable)} sewer`,
      "Known public utility availability"
    ],
    ["City-side baseline", formatCurrency(siteSummary.utilityStatus.citySideBaseline), "Current known public utility anchor"],
    ["Access / parking", siteSummary.parkingAccessStatus.label, siteSummary.parkingAccessStatus.summary],
    [
      "Parking requirement",
      formatBoolean(keyFacts.offStreetParkingRequired, "Required", "Not required"),
      "Off-street parking burden remains parcel-application sensitive"
    ]
  ];

  const zoningMatrixRows = [
    ["District fit", zoning.status, zoning.conclusion],
    ["Parcel application", zoning.parcelApplicationBestRead, "Parcel-specific code reading still controls execution"],
    [
      "STR registration",
      formatBoolean(zoning.keyFacts.strRegistrationRequired, "Required", "Not required"),
      `${formatCurrency(zoning.keyFacts.strRegistrationFee)} fee | ${formatCurrency(zoning.keyFacts.failureToRegisterPenalty)} penalty exposure`
    ],
    ["Rental inspection district", zoning.keyFacts.rentalInspectionDistrictStatus, "Operational compliance still matters"],
    ["Occupancy rule", zoning.keyFacts.occupancyRule, zoning.keyFacts.relatedOccupancyRule],
    ["Building code / inspection", zoning.keyFacts.buildingCodeRequirement, zoning.keyFacts.inspectionApproach]
  ];

  const conceptFitRows = [
    ["Lead concept", keyFacts.leadConcept, siteSummary.leadConceptFit],
    ["Parcel conclusion", parcel.status, parcel.conclusion],
    ["Regulatory conclusion", zoning.status, zoning.conclusion],
    ["Overall site posture", siteSummary.overallPosture, "Top-line land and entitlement read remains workable, not closed"],
    ["Fallback concept", keyFacts.fallbackConcept, "Visible backup path remains available if the lead stack weakens"]
  ];

  return (
    <div className="stack-xl">
      <PremiumHero
        eyebrow="Land Screen"
        title="Readable parcel fit. Workable regulatory posture."
        subtitle="The land case now reads quickly. The current question is whether parcel fit, physical posture, and zoning application stay inside a bounded execution box. Right now they do."
        backgroundImage="/comps/104-f-st-map.jpg"
        backgroundLabel="Grounded local map context"
        badges={
          <>
            <SignalPill label={siteSummary.overallPosture} tone="positive" />
            <SignalPill label={parcel.status} tone="neutral" />
            <SignalPill label={zoning.status} tone="warning" />
          </>
        }
        actions={
          <>
            <NavLink to="/parcel" className="link-button link-button-primary">
              Open Parcel
            </NavLink>
            <NavLink to="/zoning" className="link-button link-button-secondary">
              Open Zoning
            </NavLink>
          </>
        }
        aside={
          <div className="stack-md">
            <div className="stack-xs">
              <span className="card-eyebrow">At a Glance</span>
              <div style={{ fontSize: "1.8rem", lineHeight: 1, color: "#f6fbff", letterSpacing: "-0.04em" }}>
                {keyFacts.zoning}
              </div>
              <p style={{ margin: 0, color: "rgba(223,233,248,0.78)", lineHeight: 1.5 }}>
                {siteSummary.leadConceptFit}
              </p>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "12px"
              }}
            >
              {[
                ["Lot size", `${keyFacts.parcelArea.squareFeet.toLocaleString()} sf`],
                ["Frontage", `${keyFacts.frontageFeet} ft`],
                ["Depth", `${keyFacts.depthFeet} ft`],
                ["Open gates", `${combinedGateCount}`]
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    borderRadius: "18px",
                    padding: "14px",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)"
                  }}
                >
                  <div style={{ fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(198,211,234,0.62)" }}>
                    {label}
                  </div>
                  <div style={{ marginTop: "8px", color: "#f4f8ff", lineHeight: 1.35 }}>{value}</div>
                </div>
              ))}
            </div>
            <div
              style={{
                overflow: "hidden",
                borderRadius: "18px",
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.04)"
              }}
            >
              <img
                src="/comps/104-f-st-streetview.jpg"
                alt="Grounded neighborhood street context near 400 Norwood"
                style={{ display: "block", width: "100%", height: "160px", objectFit: "cover" }}
              />
            </div>
          </div>
        }
      />

      <KpiRail
        items={[
          {
            label: "Overall posture",
            value: siteSummary.overallPosture,
            note: "Top-line site and regulatory read",
            tone: "positive"
          },
          {
            label: "Parcel fit",
            value: parcel.status,
            note: `${keyFacts.parcelArea.squareFeet.toLocaleString()} sf compact infill lot`,
            tone: "neutral"
          },
          {
            label: "Flood / slope",
            value: `${siteSummary.floodStatus.label} / ${siteSummary.slopeStatus.label}`,
            note: "Physical diligence is legible and bounded",
            tone: "neutral"
          },
          {
            label: "Regulatory gates",
            value: `${combinedGateCount}`,
            note: "Finite parcel and code items still open",
            tone: "warning"
          }
        ]}
      />

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
        }}
      >
        <DataTableCard
          eyebrow="Parcel Identity"
          title="Site facts table"
          description="The parcel identity should clear in one pass: lot size, geometry, district, and baseline tax facts."
          columns={["Field", "Current read"]}
          rows={siteFactsRows}
        />

        <DataTableCard
          eyebrow="Physical Site Posture"
          title="Flood, slope, GIS, and concept fit"
          description="Physical posture is compressed into a proof table instead of spread across explanatory cards."
          columns={["Layer", "Current read", "What it means"]}
          rows={physicalPostureRows}
        />
      </div>

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
        }}
      >
        <DataTableCard
          eyebrow="Utility, Access & Parking"
          title="Servicing and access posture"
          description="Utilities read more like support than mystery. Access and parking still need disciplined parcel-level application."
          columns={["Layer", "Current read", "Investor meaning"]}
          rows={utilityRows}
        />

        <DataTableCard
          eyebrow="Zoning / Regulatory Fit"
          title="Compact zoning matrix"
          description="This is not blanket entitlement. It is the current workable rule set, fee posture, and compliance frame for the parcel."
          columns={["Rule layer", "Current read", "Constraint / note"]}
          rows={zoningMatrixRows}
        />
      </div>

      <DataTableCard
        eyebrow="Lead Concept Fit"
        title="Why the lead concept still fits"
        description="The lead concept remains supportable because parcel fit, site posture, and zoning application still point in the same direction."
        columns={["Layer", "Current read", "Support"]}
        rows={conceptFitRows}
        footer={
          <div className="row-wrap gap-sm">
            <StatusChip label={siteSummary.utilityStatus.label} tone="positive" />
            <StatusChip label={siteSummary.parkingAccessStatus.label} tone="warning" />
            <StatusChip label={siteSummary.gisFlagStatus.label} tone="neutral" />
          </div>
        }
      />

      <GroundedVisualStrip
        eyebrow="Grounded Context"
        title="Local map and street context"
        items={[
          {
            src: "/comps/104-f-st-map.jpg",
            alt: "Local map context near 400 Norwood",
            label: "Local map context",
            note: "The site sits inside a tight local context, not an abstract regional story."
          },
          {
            src: "/comps/110-f-st-map.jpg",
            alt: "Second local map context near 400 Norwood",
            label: "Support radius",
            note: "Nearby context remains geographically tight enough to keep the zoning and program discussion grounded."
          },
          {
            src: "/comps/104-f-st-streetview.jpg",
            alt: "Street context image near 400 Norwood",
            label: "Street context",
            note: "Street-level context supports compact infill framing better than a text wall ever will."
          },
          {
            src: "/comps/110-f-st-streetview.jpg",
            alt: "Second street context image near 400 Norwood",
            label: "Built context",
            note: "Built local context keeps the site fit and regulatory discussion tied to visible reality."
          }
        ]}
      />

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))"
        }}
      >
        <NumberedListCard
          eyebrow="Remaining Physical / Parcel Gates"
          title="Finite land-side items still open"
          items={[...siteSummary.openItems, ...parcel.openItems]}
          tone="neutral"
        />
        <NumberedListCard
          eyebrow="Remaining Regulatory Gates"
          title="Code and compliance items still open"
          items={zoning.openItems}
          tone="warning"
        />
      </div>

      <section
        style={{
          borderRadius: "24px",
          padding: "22px",
          background: "linear-gradient(180deg, rgba(9, 14, 26, 0.92), rgba(7, 11, 21, 0.82))",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 18px 42px rgba(3, 8, 20, 0.24)"
        }}
      >
        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
            alignItems: "center"
          }}
        >
          <div className="stack-xs">
            <span className="card-eyebrow">Supporting Detail</span>
            <h3 className="card-title">Open parcel and zoning detail only after the top-level fit is clear.</h3>
            <p className="card-copy">
              The investor read belongs on this page. Parcel and zoning remain supporting routes for detail review, not competing entry points.
            </p>
          </div>
          <div className="row-wrap gap-sm">
            <NavLink to="/parcel" className="link-button link-button-secondary">
              Parcel
            </NavLink>
            <NavLink to="/zoning" className="link-button link-button-secondary">
              Zoning
            </NavLink>
          </div>
        </div>
      </section>
    </div>
  );
}

export function SitePage() {
  const { data, loading, error } = useContent("site-zoning-page", loadSiteZoningPage);

  return (
    <PageContainer
      eyebrow="Site & Zoning"
      title="Readable parcel fit. Workable regulatory posture."
      lede="Parcel geometry, physical posture, utility/access support, zoning fit, and remaining gates are compressed into one investor-readable land screen."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading site and zoning...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? <SiteZoningContent data={data} /> : null}
    </PageContainer>
  );
}
